import { noddiAsync, URLKeys } from 'noddi-async';
import { NoddiButton, NoddiRadioGroup, NoddiSelect } from 'noddi-ui-web';
import { useMemo } from 'react';
import { catalogOrganizationsForBrands } from '../discountBrands';
import { usePromotionBrandScope } from '../usePromotionBrandScope';
import {
  BOOKING_EXCLUSION_MATCH,
  emptyBookingExclusion,
  EXCLUDE_IF_BOOKING_EXISTS_LABEL,
  EXCLUDE_IF_BOOKING_EXISTS_TOOLTIP,
  type BookingExclusionMatch,
  type BookingExclusionRow
} from './bookingExclusion';
import { CampaignDiscountSalesItemTagsPicker } from './pickers/CampaignDiscountSalesItemTagsPicker';
import { CampaignDiscountSalesItemsPicker } from './pickers/CampaignDiscountSalesItemsPicker';

type Props = {
  rows: BookingExclusionRow[];
  onChange: (rows: BookingExclusionRow[]) => void;
  currentBrands?: { id: number; name: string }[];
};

const MATCH_OPTIONS: { value: BookingExclusionMatch; label: string }[] = [
  { value: BOOKING_EXCLUSION_MATCH.any, label: 'Any booking' },
  { value: BOOKING_EXCLUSION_MATCH.products, label: 'Specific products' },
  { value: BOOKING_EXCLUSION_MATCH.tags, label: 'Specific product tags' }
];

export const BookingExclusionFields = ({ rows, onChange, currentBrands = [] }: Props) => {
  const { brandId, catalogServiceOrganizations } = usePromotionBrandScope();
  const { data: brand } = noddiAsync.useGet({ type: URLKeys.getBrand, input: { brandId } });

  const options = useMemo(() => {
    const marketplace =
      brand == null ? [] : [brand, ...brand.includedBrands].map(({ id, name }) => ({ value: id, label: name }));
    const stale = currentBrands
      .filter(({ id }) => !marketplace.some((option) => option.value === id))
      .map(({ id, name }) => ({ value: id, label: `${name} (no longer in the marketplace)` }));
    return [...marketplace, ...stale];
  }, [brand, currentBrands]);

  const usedBrandIds = new Set(rows.map((row) => row.brandId));
  const nextBrand = options.find((option) => !usedBrandIds.has(option.value));

  const updateRow = (index: number, patch: Partial<BookingExclusionRow>) => {
    onChange(rows.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row)));
  };

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex flex-col gap-1'>
        <p className='text-sm font-medium'>{EXCLUDE_IF_BOOKING_EXISTS_LABEL}</p>
        <p className='text-xs text-muted-foreground'>{EXCLUDE_IF_BOOKING_EXISTS_TOOLTIP}</p>
      </div>
      {rows.map((row, index) => {
        const catalog = catalogOrganizationsForBrands(catalogServiceOrganizations, {
          limitToBrands: row.brandId != null,
          applicableBrandIds: row.brandId != null ? [row.brandId] : []
        });
        const duplicate =
          row.brandId != null &&
          rows.some((other, otherIndex) => otherIndex !== index && other.brandId === row.brandId);
        return (
          <div
            key={`${row.brandId ?? 'new'}-${index}`}
            className='flex flex-col gap-3 rounded-lg border border-border p-3'
          >
            <div className='flex items-start justify-between gap-3'>
              <div className='min-w-0 flex-1'>
                <NoddiSelect
                  label='Brand'
                  options={options}
                  value={row.brandId ?? undefined}
                  onChange={(nextBrandId) => updateRow(index, { brandId: nextBrandId ?? null })}
                  placeholder='Select a brand'
                />
                {row.brandId == null && <p className='mt-1 text-xs text-destructive'>Pick a brand</p>}
                {duplicate && <p className='mt-1 text-xs text-destructive'>Each brand can be listed once</p>}
              </div>
              <NoddiButton
                variant='outline'
                size='small'
                startIcon='Trash2'
                onClick={() => onChange(rows.filter((_, rowIndex) => rowIndex !== index))}
              >
                Remove
              </NoddiButton>
            </div>
            <NoddiRadioGroup
              label='Match'
              value={row.match}
              onValueChange={(next) => {
                const match = next as BookingExclusionMatch;
                updateRow(index, {
                  match,
                  salesItemIds: match === BOOKING_EXCLUSION_MATCH.products ? row.salesItemIds : [],
                  salesItemTagIds: match === BOOKING_EXCLUSION_MATCH.tags ? row.salesItemTagIds : []
                });
              }}
              options={MATCH_OPTIONS}
              className='flex w-fit flex-wrap'
            />
            {row.match === BOOKING_EXCLUSION_MATCH.products && (
              <div className='flex flex-col gap-1'>
                <CampaignDiscountSalesItemsPicker
                  catalogOrganizations={catalog}
                  salesItemIds={row.salesItemIds}
                  onSalesItemIdsChange={(salesItemIds) => updateRow(index, { salesItemIds })}
                />
                {row.salesItemIds.length === 0 && <p className='text-xs text-destructive'>Pick at least one product</p>}
              </div>
            )}
            {row.match === BOOKING_EXCLUSION_MATCH.tags && (
              <div className='flex flex-col gap-1'>
                <CampaignDiscountSalesItemTagsPicker
                  catalogOrganizations={catalog}
                  salesItemTagIds={row.salesItemTagIds}
                  onSalesItemTagIdsChange={(salesItemTagIds) => updateRow(index, { salesItemTagIds })}
                />
                {row.salesItemTagIds.length === 0 && (
                  <p className='text-xs text-destructive'>Pick at least one product tag</p>
                )}
              </div>
            )}
          </div>
        );
      })}
      <div>
        <NoddiButton
          variant='outline'
          size='small'
          startIcon='Plus'
          disabled={nextBrand == null}
          onClick={() => {
            if (nextBrand == null) {
              return;
            }
            onChange([...rows, emptyBookingExclusion(nextBrand.value)]);
          }}
        >
          Add brand
        </NoddiButton>
      </div>
    </div>
  );
};
